import { useState, useRef, useEffect, useMemo } from 'react';
import { MessageSquare } from 'lucide-react';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

const ChatSection = ({ chatMessages, chatInput, setChatInput, handleChatSubmit, isLoading: externalLoading, samplePrompts = [], uid, permittedModels }) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [messageAreaHeight, setMessageAreaHeight] = useState(null); // dynamic message area height
  const [isMobile, setIsMobile] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // ref to the scroll container
  const footerRef = useRef(null); // NEW: measure the actual footer area (prompts + input)

  const inputRef = useRef(null);

  // NEW: track input area height so messages container can reserve space (kept for backward compatibility)
  const [inputHeight, setInputHeight] = useState(120);
  // NEW: measured footer height (sample prompts row + ChatInput wrapper)
  const [footerHeight, setFooterHeight] = useState(0);

  // NEW: derive streaming state from messages so loader appears after redirect
  const { isStreaming, showSkeletonBeforeFirstToken } = useMemo(() => {
    const msgs = Array.isArray(chatMessages) ? chatMessages : [];
    if (msgs.length === 0) return { isStreaming: false, showSkeletonBeforeFirstToken: false };

    let lastUserIdx = -1, lastAiIdx = -1;
    for (let i = 0; i < msgs.length; i++) {
      const s = (msgs[i]?.sender || '').toLowerCase();
      if (s === 'user') lastUserIdx = i;
      if (s === 'ai') lastAiIdx = i;
    }

    // Case 1: last message is user (no AI message yet) -> streaming expected
    if (lastUserIdx > lastAiIdx) {
      return { isStreaming: true, showSkeletonBeforeFirstToken: true };
    }

    // Case 2: last message is AI; check status and text
    const lastAi = lastAiIdx >= 0 ? msgs[lastAiIdx] : null;
    const aiStatus = (lastAi?.status || '').toLowerCase();
    const aiText = (lastAi?.text || '').trim();

    if (aiStatus === 'streaming') {
      return { isStreaming: true, showSkeletonBeforeFirstToken: aiText.length === 0 };
    }

    return { isStreaming: false, showSkeletonBeforeFirstToken: false };
  }, [chatMessages]);

  // Use external loading state if provided, otherwise use internal loading state
  const computedLoading = externalLoading !== undefined ? (externalLoading || isStreaming) : (internalLoading || isStreaming);

  // Measure footer height (prompts + input) so we can reserve exact space
  useEffect(() => {
    if (!footerRef.current) return;
    const measure = () => {
      const h = footerRef.current ? footerRef.current.getBoundingClientRect().height : 0;
      setFooterHeight(Math.ceil(h || 0));
    };

    // initial measure
    requestAnimationFrame(measure);

    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      const ro = new ResizeObserver(() => requestAnimationFrame(measure));
      ro.observe(footerRef.current);
      return () => ro.disconnect();
    }

    // fallback: window resize
    const onResize = () => requestAnimationFrame(measure);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [footerRef, samplePrompts, inputHeight]);

  // NEW: Recompute message area height for mobile ( < 768px ) when footerHeight or viewport size changes
  useEffect(() => {
    const compute = () => {
      if (typeof window === 'undefined') return;
      if (!isMobile) { setMessageAreaHeight(null); return; }
      setMessageAreaHeight(`calc(100dvh - ${footerHeight}px)`);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [footerHeight, isMobile]);

  // Detect mobile viewport
  useEffect(() => {
    const detect = () => setIsMobile(window.innerWidth < 768);
    detect();
    window.addEventListener('resize', detect);
    return () => window.removeEventListener('resize', detect);
  }, []);

  // Auto-scroll removed: manual scrolling can be triggered if/when needed.
  // (Previously we scrolled on chatMessages / chatInput / footerHeight changes;
  //  that behaviour has been removed to prevent unwanted jumps while typing.)

  const wrappedHandleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    // Only manage loading state internally if not provided externally
    if (externalLoading === undefined) setInternalLoading(true);

    try {
      await handleChatSubmit(e);
    } finally {
      // Clean up internal loading state
      if (externalLoading === undefined) setInternalLoading(false);
    }
  };

  const onSelectSample = (prompt) => {
    setChatInput(prompt);
    // focus the textarea and move caret to end
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        try { el.focus(); } catch(e){/* ignore */ }
        if (el.setSelectionRange) {
          const len = (prompt || '').length;
          el.setSelectionRange(len, len);
        }
      }
    }, 50);
  };

  const prevMessagesLengthRef = useRef(chatMessages.length);
  useEffect(() => {
    if (!messagesEndRef.current) return;
    const prevLength = prevMessagesLengthRef.current;
    const nextLength = chatMessages.length;
    prevMessagesLengthRef.current = nextLength;
    if (nextLength === 0 || nextLength <= prevLength) return;
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: prevLength === 0 ? 'auto' : 'smooth' });
    });
  }, [chatMessages]);

  return (
    <div 
      className="bg-gradient-to-b w-full from-gray-50 to-white flex flex-col h-full overflow-hidden"
      style={isMobile ? { height: '100dvh', maxHeight: '100dvh' } : { height: '100%', maxHeight: 'calc(100vh - 200px)' }}
    >
      {/* Chat Messages with proper overflow */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto px-2 sm:px-4 py-4"
        style={{ 
          boxSizing: 'border-box',
          scrollbarWidth: 'thin',
          scrollbarColor: '#CBD5E0 transparent',
          backgroundImage: 'radial-gradient(circle at 100% 0%, rgba(240, 249, 255, 0.15) 0%, transparent 60%), radial-gradient(circle at 0% 100%, rgba(236, 253, 245, 0.15) 0%, transparent 60%)',
          WebkitOverflowScrolling: 'touch',
          height: isMobile ? (messageAreaHeight || 'calc(100dvh - 160px)') : undefined,
          maxHeight: isMobile ? (messageAreaHeight || 'calc(100dvh - 160px)') : '100%',
          flex: '1 1 0%',
          overflowX: 'hidden',
          paddingBottom: '1rem',
        }}
      >
        <div className="space-y-4" style={{ minWidth: 0 /* prevents flex overflow on mobile */ }}>
          {chatMessages.length === 0 ? (
            <div className="text-center mt-6 sm:mt-12 max-w-md mx-auto">
              <div className="mb-4 bg-blue-50 p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <MessageSquare className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold mb-2 text-gray-800">How can I help you learn?</h4>
              <p className="text-sm text-gray-600">Ask me questions about this lesson to deepen your understanding.</p>
            </div>
          ) : (
            chatMessages.map((message, index) => (
              <ChatMessage key={message.id || index} message={message} />
            ))
          )}
          
          {/* NEW: minimal skeleton bubble before first AI token arrives */}
          {showSkeletonBeforeFirstToken && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1">
                <div className="animate-pulse space-y-2">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-4/6" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/6" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} className="h-4" />
        </div>
      </div>

      <div ref={footerRef} className="mt-auto bg-white bg-opacity-90 p-4 border-t flex-none">
        {/* NEW: AI-generated sample prompt chips */}
        {samplePrompts && samplePrompts.length > 0 && (
          <div className="mb-2">
            {/* Slideable row on small screens; wrapped on md+ via CSS if desired */}
            <div
              className="flex gap-3 py-1"
              style={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                msOverflowStyle: 'none',    // hide scrollbar IE/Edge
                scrollbarWidth: 'none',     // hide scrollbar Firefox
                scrollSnapType: 'x mandatory' // enable snap
              }}
            >
              {samplePrompts.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onSelectSample(p)}
                  className="flex-shrink-0 text-sm px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 whitespace-normal"
                  style={{
                    minWidth: 140,
                    scrollSnapAlign: 'start' // snap alignment
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <ChatInput
          chatInput={chatInput}
          setChatInput={setChatInput}
          handleChatSubmit={wrappedHandleChatSubmit}
          isLoading={computedLoading}
          inputRef={inputRef}
          onHeightChange={(h) => setInputHeight(h || 120)} 
          uid={uid}
          permittedModels={permittedModels}
        />
      </div>
    </div>
  );
};

export default ChatSection;